import type { StoredSession } from '@blooq/chat-store'
import {
  IonAlert,
  IonButton,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonMenu,
  IonMenuToggle,
  IonNote,
  IonTitle,
  IonToolbar,
} from '@ionic/react'
import { addOutline, trashOutline } from 'ionicons/icons'
import { useState } from 'react'

import { tap } from '../lib/native'

import { DotSphere, STILL } from './DotSphere'

/*
 * Past conversations, in a drawer.
 *
 * The server keeps no transcript — it hands back one step at a time and forgets
 * — so "what did I book last month" can only exist on the device. See
 * packages/chat-store for where it lives and why the privacy note there is
 * load-bearing: what is stored is what the customer typed, usually a name and a
 * mobile number.
 *
 * Which is why "Forget these" is here rather than buried in a settings screen
 * this app does not have. A customer on a shared phone needs it within reach of
 * the list itself.
 */

export function HistoryMenu({
  menuId,
  contentId,
  accent,
  sessions,
  currentId,
  onOpenSession,
  onNewConversation,
  onClearHistory,
}: {
  menuId: string
  contentId: string
  /** The business's colour, for the empty state's sphere. */
  accent?: string
  sessions: StoredSession[]
  currentId?: string
  onOpenSession: (session: StoredSession) => void
  onNewConversation: () => void
  onClearHistory: () => void
}) {
  /*
   * Clearing is confirmed, and it is the only thing in the app that is.
   *
   * Everything else a customer can tap is recoverable — a wrong date is one
   * back-tap away, a cancelled booking is a phone call. This is the one action
   * that destroys something that exists nowhere else.
   */
  const [confirming, setConfirming] = useState(false)

  return (
    <IonMenu menuId={menuId} contentId={contentId} type="overlay" side="start">
      {/* ion-no-border, as on the main header: the drawer's title sits on the
          same paper as its list, not on a Material bar with a shadow. */}
      <IonHeader className="ion-no-border">
        <IonToolbar className="blooq-toolbar">
          <IonTitle className="blooq-drawer__title">Your conversations</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="blooq-drawer">
        <IonMenuToggle autoHide={false}>
          <div className="blooq-drawer__pad">
            <IonButton
              expand="block"
              onClick={() => {
                tap()
                onNewConversation()
              }}
            >
              <IonIcon slot="start" icon={addOutline} />
              New conversation
            </IonButton>
          </div>
        </IonMenuToggle>

        {sessions.length === 0 ? (
          /* The front door's sphere, resting, above the one line there is to
             say: an empty list drawn as a picture of the thing that fills it. */
          <div className="blooq-history__empty">
            <div className="blooq-history__sphere" aria-hidden="true">
              <DotSphere
                accent={accent ?? '#0369a1'}
                mode="idle"
                level={STILL}
                points={160}
                interactive={false}
              />
            </div>
            <p>
              Conversations you have here are kept on this device, so you can look
              back at what you booked.
            </p>
          </div>
        ) : (
          /* No divider lines. Each conversation is a soft rounded row, the
             same shape as every other tappable thing in the app. */
          <IonList lines="none" className="blooq-history__list">
            {sessions.map((session) => (
              <IonMenuToggle key={session.id} autoHide={false}>
                <IonItem
                  button
                  detail={false}
                  className={session.id === currentId ? 'blooq-history__current' : undefined}
                  onClick={() => {
                    tap()
                    onOpenSession(session)
                  }}
                >
                  <IonLabel>
                    <h3>{session.title}</h3>
                    <p className="blooq-history__when">
                      {new Date(session.startedAt).toLocaleDateString(undefined, {
                        day: 'numeric',
                        month: 'short',
                      })}
                      {session.done ? '' : ' · unfinished'}
                    </p>
                  </IonLabel>
                </IonItem>
              </IonMenuToggle>
            ))}
          </IonList>
        )}

        {sessions.length > 0 ? (
          <div className="blooq-drawer__pad">
            <IonButton
              expand="block"
              fill="clear"
              color="danger"
              onClick={() => setConfirming(true)}
            >
              <IonIcon slot="start" icon={trashOutline} />
              Forget these
            </IonButton>
            <IonNote className="blooq-meta">
              Kept on this phone only. Nothing here is sent anywhere.
            </IonNote>
          </div>
        ) : null}
      </IonContent>

      <IonAlert
        cssClass="blooq-alert"
        isOpen={confirming}
        header="Forget these conversations?"
        message="They are only on this device, so this cannot be undone. Your bookings themselves are not affected."
        buttons={[
          { text: 'Keep them', role: 'cancel' },
          {
            text: 'Forget',
            role: 'destructive',
            handler: () => {
              onClearHistory()
            },
          },
        ]}
        onDidDismiss={() => setConfirming(false)}
      />
    </IonMenu>
  )
}
